<template>
  <a-modal
    :open="visible"
    :title="`批量检测 — ${proxyIds.length} 个节点`"
    width="960px"
    :mask-closable="!busy"
    :closable="!busy"
    :footer="null"
    @cancel="close"
  >
    <div v-if="busy && !rows.length" class="hc-waiting">
      <a-spin size="large" />
      <div class="hc-waiting-text">正在检测 {{ proxyIds.length }} 个节点，通常需要几秒到几十秒</div>
    </div>

    <template v-else-if="rows.length">
      <a-alert
        class="hc-banner"
        show-icon
        :type="summary.abnormal === 0 ? 'success' : summary.unavailable > 0 ? 'error' : 'warning'"
        :message="bannerText"
      />

      <div class="hc-stats">
        <span class="hc-stat">共 <b>{{ summary.total }}</b></span>
        <span class="hc-stat ok">可用 <b>{{ summary.available }}</b></span>
        <span class="hc-stat bad" :class="{ zero: !summary.unavailable }">不可用 <b>{{ summary.unavailable }}</b></span>
        <span class="hc-stat warn" :class="{ zero: !summary.skipped }">已跳过 <b>{{ summary.skipped }}</b></span>
        <span class="hc-stat bad" :class="{ zero: !summary.duplicateGroups }" title="不同服务器使用了同一个出口 IP；同一服务器上的多个 proxy 共用出口 IP 是正常的">重复出口 IP <b>{{ summary.duplicateGroups }}</b> 组</span>
        <a-radio-group v-model:value="filter" size="small" button-style="solid" class="hc-filter">
          <a-radio-button value="all">全部 {{ summary.total }}</a-radio-button>
          <a-radio-button value="abnormal">需关注 {{ summary.abnormal }}</a-radio-button>
          <a-radio-button value="duplicate">重复 IP</a-radio-button>
        </a-radio-group>
      </div>

      <a-table
        :columns="columns"
        :data-source="visibleRows"
        :pagination="false"
        :loading="busy"
        size="small"
        row-key="proxy_id"
        table-layout="fixed"
        :scroll="{ y: 400 }"
      >
        <template #emptyText>当前筛选下没有记录</template>
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'status'">
            <a-tag :color="statusColor(record.status)" class="hc-tag">{{ statusLabel(record.status) }}</a-tag>
            <div v-if="record.status !== 'skipped'" class="hc-sub mono">{{ record.rtt_ms }} ms</div>
          </template>
          <template v-else-if="column.key === 'proxy'">
            <div class="mono">{{ record.username }}</div>
            <div class="hc-sub">#{{ record.proxy_id }}</div>
          </template>
          <template v-else-if="column.key === 'node'">
            <div class="hc-ellipsis" :title="record.node_name">{{ record.node_name }}</div>
            <div class="hc-sub mono">{{ record.agent_ip || '—' }}:{{ record.agent_port || '—' }}</div>
          </template>
          <template v-else-if="column.key === 'exit'">
            <template v-if="record.detected_ipv4">
              <span class="mono">{{ record.detected_ipv4 }}</span>
              <a-tag v-if="duplicateCount(record.detected_ipv4) > 1" color="red" class="hc-tag">{{ duplicateCount(record.detected_ipv4) }} 台服务器重复</a-tag>
              <div class="hc-sub hc-ellipsis" :title="record.ip_location">{{ record.ip_location || '归属地未知' }}</div>
            </template>
            <template v-else-if="record.status === 'available'">
              <a-tag color="orange" class="hc-tag" :title="record.ipv4_error">出口 IP 检测失败</a-tag>
            </template>
            <span v-else class="hc-sub">—</span>
          </template>
          <template v-else-if="column.key === 'detail'">
            <span v-if="reasonsOf(record).length" class="hc-error hc-ellipsis" :title="reasonsOf(record).join('；')">{{ reasonsOf(record).join('；') }}</span>
            <span v-else class="hc-sub hc-ellipsis" :title="formatSteps(record.steps)">{{ formatSteps(record.steps) }}</span>
          </template>
        </template>
      </a-table>
    </template>

    <a-alert v-else-if="loadError" type="error" show-icon :message="loadError" />

    <div class="hc-actions">
      <a-button v-if="summary.abnormal" :disabled="busy" @click="copyAbnormal">复制异常项</a-button>
      <a-button v-if="summary.abnormal" :disabled="busy" @click="recheckAbnormal">重测异常项</a-button>
      <a-button v-if="!busy || rows.length" :disabled="busy" @click="run">重新检测全部</a-button>
      <a-button type="primary" :disabled="busy" @click="close">关闭</a-button>
    </div>
  </a-modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import { batchHealthcheckProxy, type BatchHealthcheckResult, type HealthcheckStep } from '../api/proxies';
import { countDetectedIPv4 } from './healthcheckDuplicateIPs';
import {
  abnormalReasons,
  abnormalReport,
  filterResults,
  mergeResults,
  sortResults,
  summarize,
  type HealthcheckFilter,
} from './healthcheckView';

const props = defineProps<{ visible: boolean; proxyIds: number[] }>();
const emit = defineEmits<{ (e: 'update:visible', value: boolean): void }>();

const busy = ref(false);
const rows = ref<BatchHealthcheckResult[]>([]);
const loadError = ref('');
const filter = ref<HealthcheckFilter>('all');
const batchLimit = 100;

const ipCounts = computed(() => countDetectedIPv4(rows.value));
const summary = computed(() => summarize(rows.value, ipCounts.value));
const visibleRows = computed(() => sortResults(filterResults(rows.value, filter.value, ipCounts.value), ipCounts.value));
const duplicateCount = (ip?: string): number => (ip ? ipCounts.value.get(ip.trim()) || 0 : 0);
const reasonsOf = (record: BatchHealthcheckResult) => abnormalReasons(record, ipCounts.value);

const bannerText = computed(() => {
  const s = summary.value;
  if (s.abnormal === 0) return `全部 ${s.total} 个节点可用，出口 IP 无跨服务器重复`;
  const parts: string[] = [];
  if (s.unavailable) parts.push(`${s.unavailable} 个不可用`);
  if (s.skipped) parts.push(`${s.skipped} 个已跳过`);
  const other = s.abnormal - s.unavailable - s.skipped;
  if (other > 0) parts.push(`${other} 个出口 IP 异常`);
  return `${s.total} 个中有 ${parts.join('、')}，已排在最前面`;
});

const columns = [
  { title: '结果', key: 'status', width: 96 },
  { title: '账号', key: 'proxy', width: 120 },
  { title: '节点', key: 'node', width: 190 },
  { title: '实际出口 IPv4', key: 'exit', width: 250 },
  { title: '详情', key: 'detail' },
];

const statusColor = (status: string) => {
  if (status === 'available') return 'green';
  if (status === 'unavailable') return 'red';
  return 'orange';
};

const statusLabel = (status: string) => {
  if (status === 'available') return '可用';
  if (status === 'unavailable') return '不可用';
  if (status === 'skipped') return '已跳过';
  return status;
};

const formatSteps = (steps: HealthcheckStep[] = []) =>
  steps.map((step) => `${step.step} ${step.ms}ms`).join(' · ') || '检测通过';

const errorText = (e: any) => e?.response?.data?.error?.message || e?.message || '批量检测失败';

const run = async () => {
  if (busy.value || props.proxyIds.length === 0) return;
  if (props.proxyIds.length > batchLimit) {
    loadError.value = `单次最多检测 ${batchLimit} 个节点，请减少勾选数量后重试。`;
    return;
  }
  busy.value = true;
  rows.value = [];
  loadError.value = '';
  filter.value = 'all';
  try {
    rows.value = (await batchHealthcheckProxy(props.proxyIds)).data.results;
  } catch (e: any) {
    loadError.value = errorText(e);
  } finally {
    busy.value = false;
  }
};

// Only the proxies that need attention are probed again; their new rows replace the old ones.
const recheckAbnormal = async () => {
  if (busy.value) return;
  const ids = rows.value.filter((r) => abnormalReasons(r, ipCounts.value).length > 0).map((r) => r.proxy_id);
  if (!ids.length) return;
  busy.value = true;
  try {
    const response = await batchHealthcheckProxy(ids);
    rows.value = mergeResults(rows.value, response.data.results);
  } catch (e: any) {
    message.error(errorText(e));
  } finally {
    busy.value = false;
  }
};

const copyAbnormal = async () => {
  try {
    await navigator.clipboard.writeText(abnormalReport(rows.value, ipCounts.value));
    message.success('已复制异常项');
  } catch {
    message.error('复制失败，请手动选取');
  }
};

const close = () => {
  if (!busy.value) emit('update:visible', false);
};

watch(
  () => props.visible,
  (visible) => {
    if (visible) run();
    else {
      rows.value = [];
      loadError.value = '';
    }
  },
  { immediate: true },
);
</script>

<style scoped>
.mono { font-family: ui-monospace, Menlo, monospace; }
.hc-waiting { padding: 36px 0; text-align: center; }
.hc-waiting-text { margin-top: 12px; color: #6b7280; }
.hc-banner { margin-bottom: 12px; }
.hc-stats { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 18px; margin-bottom: 10px; color: #6b7280; }
.hc-stat b { font-size: 16px; font-weight: 600; color: #111827; }
.hc-stat.ok b { color: #389e0d; }
.hc-stat.bad b { color: #cf1322; }
.hc-stat.warn b { color: #d48806; }
.hc-stat.zero b { color: #9ca3af; }
.hc-filter { margin-left: auto; }
.hc-tag { margin-inline-end: 0; }
.hc-sub { font-size: 12px; color: #9ca3af; line-height: 1.4; }
.hc-error { display: block; color: #cf1322; }
.hc-ellipsis { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hc-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px; }
@media (max-width: 720px) {
  .hc-filter { margin-left: 0; }
}
</style>
